import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import MembershipPage from '@/pages/MembershipPage';
import { apiService } from '@/lib/api';
import LandingPage from '@/pages/LandingPage';
import Dashboard from '@/pages/Dashboard';
import DashboardV2 from '@/pages/DashboardV2';
import DashboardOptionA from '@/pages/DashboardOptionA';
import DashboardOptionB from '@/pages/DashboardOptionB';
import MigrationScreen from '@/pages/MigrationScreen';
import TryDemo from '@/pages/TryDemo';
import Campaign from '@/pages/Campaign';
import FirstDraw from '@/pages/FirstDraw';
import DemoModeBanner from '@/components/DemoModeBanner';
import ProjectDetails from '@/pages/ProjectDetails';
import BidSheets from '@/pages/BidSheets';
import BidSheet from '@/pages/BidSheet';
import Templates from '@/pages/Templates';
import TemplateDetails from '@/pages/TemplateDetails';
import BankTemplates from '@/pages/BankTemplates';
import Lenders from '@/pages/Lenders';
import Profile from '@/pages/Profile';
import Settings from '@/pages/Settings';
import WhatsNewHistoryPage from '@/pages/WhatsNewHistoryPage';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import TeamMemberSignup from '@/pages/TeamMemberSignup';
import Subscribe from '@/pages/Subscribe';
import VerifyEmail from '@/pages/VerifyEmail';
import ResetPassword from '@/pages/ResetPassword';
import AcceptInvite from '@/pages/AcceptInvite';
import AuthCallback from '@/pages/AuthCallback';
import UploadForms from '@/pages/UploadForms';
import ContractorPortalTokenRedirect from '@/pages/ContractorPortalTokenRedirect';
import NotFoundRoute from '@/pages/NotFoundRoute';
import Pricing from '@/pages/Pricing';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import Reports from '@/pages/Reports';
import GlobalContractors from '@/pages/GlobalContractors';
import GlobalDocuments from '@/pages/GlobalDocuments';
import FinancialAnalysis from '@/pages/FinancialAnalysis';
import DealUnderwriter from '@/pages/DealUnderwriter';
import AnalysisSuite from '@/pages/AnalysisSuite';
import AwardCascadePreview from '@/pages/AwardCascadePreview';
import ContractorWorkspace from '@/pages/ContractorWorkspace';
import ProjectLifecycle from '@/pages/ProjectLifecycle';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import Disclaimer from '@/pages/Disclaimer';
import Contact from '@/pages/Contact';
import About from '@/pages/About';
import Admin from '@/pages/Admin';
import FeatureAnnouncementPage from '@/pages/FeatureAnnouncementPage';
import AdminLogin from '@/pages/AdminLogin';
import ProjectHub from '@/pages/ProjectHub';
import ProjectHubDemo from '@/pages/ProjectHubDemo';
import StartProjectPreview from '@/pages/StartProjectPreview';
import OnboardingPreview from '@/pages/OnboardingPreview';
import Onboarding from '@/pages/Onboarding';
import LenderViewPreview from '@/pages/LenderViewPreview';
import LenderPortfolio from '@/pages/LenderPortfolio';
import LenderDemoAccess from '@/pages/LenderDemoAccess';
import LenderDemoEntry from '@/pages/lender/LenderDemoEntry';
import LenderLoans from '@/pages/lender/LenderLoans';
import LenderBankUsers from '@/pages/lender/LenderBankUsers';
import LenderDraws from '@/pages/lender/LenderDraws';
import LenderDrawReview from '@/pages/lender/LenderDrawReview';
import LenderBorrowerView from '@/pages/lender/LenderBorrowerView';
import LenderGate from '@/components/lender/LenderGate';
import LenderApply from '@/pages/lender/LenderApply';
import LenderApplicationStatus from '@/pages/lender/LenderApplicationStatus';
import LenderApplications from '@/pages/lender/LenderApplications';
import LenderApplicationReview from '@/pages/lender/LenderApplicationReview';
import LenderAspirinDemo from '@/pages/LenderAspirinDemo';
import OnboardingAspirinPreview from '@/pages/OnboardingAspirinPreview';
import { ResourcesHub, ClusterHub, ArticlePage, ProductPage, GlossaryIndex, GlossaryTerm, ToolsPage } from '@/pages/seo/SeoPages';
import { DrawScheduleBuilder, DrawReadinessChecklist, DrawInterestEstimator } from '@/pages/seo/SeoTools';
import PartnersPage from '@/pages/seo/PartnersPage';
import { PRODUCTS } from '@/seo/data';
import NewProjectVision from '@/pages/NewProjectVision';
import BatchBPreview from '@/pages/BatchBPreview';
import ReceiptAnnotationPreview from '@/pages/ReceiptAnnotationPreview';
import DocsModalCompare from '@/pages/DocsModalCompare';
import ProjectHubPhase2Preview from '@/pages/ProjectHubPhase2Preview';
import MultiUnitSchedulePreview from '@/pages/MultiUnitSchedulePreview';
import StandaloneSchedulePage from '@/pages/StandaloneSchedulePage';
import SchedulePage from '@/pages/SchedulePage';
import CascadeRespondPage from '@/pages/CascadeRespondPage';
import OwnerActionPage from '@/pages/OwnerActionPage';
import ScheduleQuestionnairePage from '@/pages/ScheduleQuestionnairePage';
import PreviewEnvBanner from '@/components/PreviewEnvBanner';
import ProjectSchedule from '@/pages/ProjectSchedule';
import BidRequestDetail from '@/pages/BidRequestDetail';
import ContractorBidPortal from '@/pages/ContractorBidPortal';
import ContractorCompliancePortal from '@/pages/ContractorCompliancePortal';
import ContractorPortalV2 from '@/pages/ContractorPortalV2';
import ContractorLogin from '@/pages/ContractorLogin';
import ContractorForgotPassword from '@/pages/ContractorForgotPassword';
import ContractorResetPassword from '@/pages/ContractorResetPassword';
import ContractorSignup from '@/pages/ContractorSignup';
import ContractorPortalPreview from '@/pages/ContractorPortalPreview';
import QuickCapturePreview from '@/pages/QuickCapturePreview';
import AssistantPreview from '@/pages/AssistantPreview';
import AssistantWidgetPreview from '@/pages/AssistantWidgetPreview';
import FinancialCommandCenterPreview from '@/pages/FinancialCommandCenterPreview';
import CommandCenterLayoutOptions from '@/pages/CommandCenterLayoutOptions';
import ShareWithBankPreview from '@/pages/ShareWithBankPreview';
import { QuickCaptureFAB } from '@/components/QuickCapture';
import DashboardDesignPreview from '@/pages/DashboardDesignPreview';
import DashboardDirectionPreview from '@/pages/DashboardDirectionPreview';
import CentralDashboardPreview from '@/pages/CentralDashboardPreview';
import DashboardOptions from '@/pages/DashboardOptions';
import AllInCostPreview from '@/pages/AllInCostPreview';
import QuickDrawPreview from '@/pages/QuickDrawPreview';
import DrawScoreGatePreview from '@/pages/DrawScoreGatePreview';
import ConfirmDraw from '@/pages/ConfirmDraw';
import HeroVariants from '@/pages/HeroVariants';
import DrawConfirmationManager from '@/components/DrawConfirmationManager';
import BuilderCompanyPreview from '@/pages/BuilderCompanyPreview';
import DemoAccess from '@/pages/DemoAccess';
import DemoLeads from '@/pages/DemoLeads';
import CompleteSignup from '@/pages/CompleteSignup';
import ChangeOrderPreview from '@/pages/ChangeOrderPreview';
import VoiceChatbot from '@/components/VoiceChatbot';
import AssistantWidget from '@/components/AssistantWidget';
import BetaBanner from '@/components/BetaBanner';
import FeatureAnnouncementModal from '@/components/FeatureAnnouncementModal';
import WhatsNewModal from '@/components/WhatsNewModal';
import { SidebarLayout } from '@/components/Sidebar';
import AddOnUpgrade from '@/components/AddOnUpgrade';
import AddOnStore from '@/pages/AddOnStore';
import ContractorPaymentReport from '@/pages/ContractorPaymentReport';
import { hasAdvancedModules, ownsAddon } from '@/lib/featureFlags';
import { WelcomeTour, useTour } from '@/components/WelcomeTour';
import { MessageCircle, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/App.css';

// Protected route wrapper with sidebar
const ProtectedRoute = ({ user, onLogout, children, requiresPayment = true, userRoles = [], customPermissions = [] }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Admins are superusers — they can navigate freely. The /admin route is
  // still reachable via sidebar. Auto-redirecting every protected route
  // hijacks normal navigation (e.g., clicking Project Hub) so we only
  // default admins to /admin on the root / dashboard route (handled below).

  return (
    <SidebarLayout user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
      {children}
    </SidebarLayout>
  );
};

// True after we've handled the one-time cold-load deep-link restore, so an
// intentional in-app "Dashboard" navigation is never hijacked back to a project.
let __coldLoadRestoreHandled = false;

// Router component that handles auth callback detection
function AppRouter({ user, onLogin, onLogout, onUpdateUser, isHydrated, showPaymentModal, onPaymentComplete, userRoles, customPermissions }) {
  const location = useLocation();
  const navigate = useNavigate();

  // First-run onboarding gate: ONLY brand-new signups (onboarding_completed
  // explicitly === false) see the guided wizard. Legacy/Google/existing users
  // have the field absent (undefined) or backfilled true by the backend, so
  // they're never pushed into it. Excludes admins, team members, lender/demo.
  const needsOnboarding = !!user && user.onboarding_completed === false
    && localStorage.getItem('onboarding_done') !== '1'
    && user.account_type !== 'lender' && user.account_type !== 'team_member'
    && !user.is_admin && !localStorage.getItem('demo_session');
  
  // Tour state at app level so it persists across navigation
  const { showTour, startTour, closeTour, hasSeenTour } = useTour();

  // Check for session_id in URL fragment (Google OAuth callback)
  // Check both React Router location and window.location for edge cases
  const isOAuthCallback = location.hash?.includes('session_id=') || 
    (typeof window !== 'undefined' && window.location.hash?.includes('session_id='));

  // Save current path for iOS Safari tab-recovery. Written to BOTH
  // sessionStorage AND localStorage — iOS wipes sessionStorage when it evicts
  // a backgrounded tab, but localStorage survives, so cold reloads can recover.
  useEffect(() => {
    if (!isOAuthCallback && user && location.pathname !== '/' && location.pathname !== '/dashboard' && location.pathname !== '/login' && location.pathname !== '/signup' && location.pathname !== '/onboarding') {
      const path = location.pathname + location.search;
      sessionStorage.setItem('lastPath', path);
      try { localStorage.setItem('drawready.lastPath', path); } catch (e) { /* ignore */ }
    }
  }, [location.pathname, location.search, user, isOAuthCallback]);

  // iOS Safari can evict a backgrounded tab and cold-reload the app onto
  // /dashboard, losing the project the user was viewing (they'd land on their
  // first active project instead). On the FIRST render after such a cold load,
  // if we're authed and sitting on /dashboard or / but have a saved deeper
  // path, restore it. Guarded by a module flag so it runs once per page load
  // and never fights an intentional "Dashboard" click during the session.
  useEffect(() => {
    if (__coldLoadRestoreHandled) return;
    __coldLoadRestoreHandled = true;
    if (isOAuthCallback || !user) return;
    const currentPath = location.pathname;
    if (currentPath !== '/' && currentPath !== '/dashboard') return;
    let saved = null;
    try { saved = localStorage.getItem('drawready.lastPath'); } catch (e) { /* ignore */ }
    if (!saved) saved = sessionStorage.getItem('lastPath');
    if (saved && saved !== '/' && saved !== '/dashboard' && saved !== currentPath) {
      navigate(saved, { replace: true });
    }
  }, []);

  // Restore path on visibility change if we ended up on wrong page
  // This handles iOS Safari's behavior of resetting state when switching apps
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        const lastPath = sessionStorage.getItem('lastPath') || localStorage.getItem('drawready.lastPath');
        const currentPath = window.location.pathname;
        
        // If we're on dashboard/home but should be elsewhere, restore
        // Check both /dashboard and / (which redirects to dashboard)
        if (lastPath && (currentPath === '/dashboard' || currentPath === '/') && lastPath !== '/dashboard' && lastPath !== '/') {
          // Use requestAnimationFrame for smoother transition after browser settles
          requestAnimationFrame(() => {
            // Double-check we're still on the wrong page
            if (window.location.pathname === '/dashboard' || window.location.pathname === '/') {
              navigate(lastPath, { replace: true });
            }
          });
        }
      }
    };
    
    // Also handle pageshow event for iOS back/forward cache
    const handlePageShow = (event) => {
      if (event.persisted && user) {
        const lastPath = sessionStorage.getItem('lastPath') || localStorage.getItem('drawready.lastPath');
        const currentPath = window.location.pathname;
        if (lastPath && lastPath !== currentPath && (currentPath === '/dashboard' || currentPath === '/')) {
          navigate(lastPath, { replace: true });
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [user, navigate]);

  // Handle OAuth callback - render AuthCallback component
  // This check is AFTER all hooks to maintain consistent hook order
  if (isOAuthCallback) {
    return <AuthCallback onLogin={onLogin} />;
  }

  // Don't render routes until hydrated to prevent incorrect redirects on iOS Safari
  // when switching back from another app
  if (!isHydrated) {
    return null;
  }

  return (
    <>
    <DemoModeBanner />
    <Routes>
      {/* Public Landing Page */}
      <Route
        path="/"
        element={
          user ? (
            user.is_admin ? <Navigate to="/admin" replace /> :
            showPaymentModal ? <Navigate to="/membership" replace /> : <Navigate to="/dashboard" replace />
          ) : (
            <LandingPage />
          )
        }
      />

      {/* Auth Pages */}
      <Route
        path="/login"
        element={
          user ? (
            user.is_admin ? <Navigate to="/admin" replace /> :
            showPaymentModal ? <Navigate to="/membership" replace /> : <Navigate to="/dashboard" replace />
          ) : (
            <Login onLogin={onLogin} />
          )
        }
      />
      <Route
        path="/signup"
        element={
          user ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <Signup onLogin={onLogin} />
          )
        }
      />
      <Route
        path="/team-signup"
        element={
          user ? (
            <Navigate to="/dashboard" replace />
          ) : (
            <TeamMemberSignup onLogin={onLogin} />
          )
        }
      />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail onLogin={onLogin} />} />
      <Route 
        path="/accept-invite" 
        element={<AcceptInvite user={user} onLogin={onLogin} />} 
      />

      {/* Public Demo Route */}
      <Route path="/demo" element={<ProjectHubDemo />} />

      {/* Lender demo — passcode gate into the seeded demo account on the real app */}
      <Route path="/bank-demo" element={<DemoAccess />} />

      {/* Public shareable "Try the demo" — isolated 1h playground with lead capture */}
      <Route path="/try-demo" element={<TryDemo onLogin={onLogin} />} />

      {/* Public guided "Build your first draw" — seeded fake data, branded PDF, no signup */}
      <Route path="/first-draw" element={<FirstDraw />} />

      {/* Public cold-email campaign landing pages (drive traffic → /signup) */}
      <Route path="/c" element={<Campaign />} />
      <Route path="/c/:slug" element={<Campaign />} />
      
      {/* Design Preview Route */}
      <Route path="/design-preview" element={<DashboardDesignPreview />} />
      <Route path="/dashboard-direction-preview" element={<DashboardDirectionPreview />} />
      <Route path="/central-dashboard-preview" element={<CentralDashboardPreview />} />
      <Route path="/dashboard-options" element={<DashboardOptions />} />
      <Route path="/all-in-cost-preview" element={<AllInCostPreview />} />
      <Route path="/quick-draw-preview" element={<QuickDrawPreview />} />
      <Route path="/draw-score-gate-preview" element={<DrawScoreGatePreview />} />
      <Route path="/confirm-draw/:token" element={<ConfirmDraw />} />
      <Route path="/hero-variants" element={<HeroVariants />} />
      <Route path="/builder-preview" element={<BuilderCompanyPreview />} />
      
      {/* Change Order Preview Route */}
      <Route path="/change-order-preview" element={<ChangeOrderPreview />} />

      {/* Quick Capture Preview Route */}
      <Route path="/quick-capture-preview" element={<QuickCapturePreview />} />

      {/* DrawReady Assistant (AI command bar) Preview Route */}
      <Route path="/assistant-preview" element={<AssistantPreview />} />

      {/* DrawReady Assistant floating-widget placement preview */}
      <Route path="/assistant-widget-preview" element={<AssistantWidgetPreview />} />

      {/* Financial Command Center Preview Route */}
      <Route path="/financial-command-center-preview" element={<FinancialCommandCenterPreview />} />
      <Route path="/command-center-layout-options" element={<CommandCenterLayoutOptions />} />
      <Route path="/share-with-bank-preview" element={<ShareWithBankPreview />} />
      <Route path="/start-project-preview" element={<StartProjectPreview />} />
      <Route path="/onboarding-preview" element={<OnboardingPreview />} />
      <Route path="/lender-view-preview" element={<LenderViewPreview />} />
      <Route path="/lender-demo" element={<LenderDemoAccess />} />
      <Route path="/lender-draw-demo" element={<Navigate to="/lender-portal-demo" replace />} />
      <Route path="/lender-portal-demo" element={<LenderAspirinDemo />} />
      <Route path="/onboarding-vision" element={<OnboardingAspirinPreview />} />
      <Route path="/new-project-vision" element={<NewProjectVision />} />
      <Route path="/lender" element={<LenderPortfolio />} />
      {/* Lender dashboard demo (Step 1: banks, roles, loans, access control) — passcode-gated */}
      <Route path="/lender/demo" element={<LenderGate><LenderDemoEntry /></LenderGate>} />
      <Route path="/lender/loans" element={<LenderGate><LenderLoans /></LenderGate>} />
      <Route path="/lender/bank-users" element={<LenderGate><LenderBankUsers /></LenderGate>} />
      <Route path="/lender/draws" element={<LenderGate><LenderDraws /></LenderGate>} />
      <Route path="/lender/draws/:drawId" element={<LenderGate><LenderDrawReview /></LenderGate>} />
      <Route path="/lender/borrower/:loanId" element={<LenderGate><LenderBorrowerView /></LenderGate>} />
      <Route path="/lender/apply" element={<LenderGate><LenderApply /></LenderGate>} />
      <Route path="/lender/application/:appId" element={<LenderGate><LenderApplicationStatus /></LenderGate>} />
      <Route path="/lender/applications" element={<LenderGate><LenderApplications /></LenderGate>} />
      <Route path="/lender/applications/:appId" element={<LenderGate><LenderApplicationReview /></LenderGate>} />
      <Route path="/batch-b-preview" element={<BatchBPreview />} />
      <Route path="/receipt-annotation-preview" element={<ReceiptAnnotationPreview />} />
      <Route path="/preview/docs-modal-compare" element={<DocsModalCompare />} />
      <Route path="/project-hub-phase2-preview" element={<ProjectHubPhase2Preview />} />
      <Route path="/multi-unit-schedule-preview" element={<MultiUnitSchedulePreview />} />
      <Route path="/award-cascade-preview" element={<AwardCascadePreview />} />
      <Route path="/contractor-workspace" element={<ContractorWorkspace />} />

      {/* Contractor Lifecycle hub — Job Site Rules + Cascade Timeline + Roster */}
      <Route
        path="/project/:projectId/lifecycle"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <ProjectLifecycle user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Complete Signup - Payment collection for Google OAuth users */}
      <Route
        path="/complete-signup"
        element={
          user ? (
            <CompleteSignup user={user} onUpdateUser={onUpdateUser} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Membership Page - shown when card is required */}
      <Route
        path="/membership"
        element={
          user ? (
            showPaymentModal ? (
              <MembershipPage user={user} onComplete={onPaymentComplete} />
            ) : (
              <Navigate to="/dashboard" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Protected Routes */}
      {/* v2.6.6 — /dashboard now renders the redesigned onboarding-first DashboardV2.
          The classic dashboard lives at /dashboard-classic and hosts the proven
          Create-Project + Excel-Import flows that V2 deep-links into. */}
      <Route
        path="/onboarding"
        element={user ? <Onboarding user={user} onUpdateUser={onUpdateUser} /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/dashboard"
        element={
          user ? (
            user.is_admin ? <Navigate to="/admin" replace /> :
            showPaymentModal ? <Navigate to="/membership" replace /> :
            needsOnboarding ? <Navigate to="/onboarding" replace /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <DashboardV2 user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/dashboard-option-a"
        element={
          user ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <DashboardOptionA user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/dashboard-option-b"
        element={
          user ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <DashboardOptionB user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/dashboard-classic"
        element={
          user ? (
            user.is_admin ? <Navigate to="/admin" replace /> :
            showPaymentModal ? <Navigate to="/membership" replace /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <Dashboard user={user} onLogout={onLogout} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/migrate"
        element={
          user ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <MigrationScreen user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/demo-leads"
        element={
          user ? (
            user?.feature_flags?.demo_owner_tools ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <DemoLeads user={user} />
            </ProtectedRoute>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/dashboard-v2-preview"
        element={
          user ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <DashboardV2 user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/schedule"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              {ownsAddon(user, 'schedule') ? <SchedulePage user={user} /> : <AddOnUpgrade moduleId="schedule" inline />}
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/add-ons"
        element={
          user ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <AddOnStore user={user} onUpdateUser={onUpdateUser} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/contractor-payments"
        element={
          user ? (
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <ContractorPaymentReport user={user} />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            <ProjectDetails user={user} onLogout={onLogout} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/project/:projectId/schedule"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <ProjectSchedule />
          </ProtectedRoute>
        }
      />
      {/* Redirect bid-sheets to Project Hub bid sheets tab (consolidated UX) */}
      <Route
        path="/bid-sheets"
        element={<Navigate to="/project-hub?tab=bidsheets" replace />}
      />
      <Route
        path="/bid-sheet/:bidSheetId"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <BidSheet user={user} onLogout={onLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <Templates user={user} onLogout={onLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/template/:templateId"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <TemplateDetails user={user} />
          </ProtectedRoute>
        }
      />
      {/* Redirect bank-templates to Project Hub bid sheets tab (consolidated UX) */}
      <Route
        path="/bank-templates"
        element={<Navigate to="/project-hub?tab=bidsheets" replace />}
      />
      <Route
        path="/lenders"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <Lenders user={user} onLogout={onLogout} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <Profile user={user} onLogout={onLogout} onUpdateUser={onUpdateUser} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <Settings user={user} />
          </ProtectedRoute>
        }
      />
      <Route
        path="/whats-new"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <WhatsNewHistoryPage user={user} />
          </ProtectedRoute>
        }
      />

      {/* Public route for contractors to upload forms (no auth required) */}
      <Route path="/upload-forms/:uploadToken" element={<UploadForms />} />

      {/* Pricing - accessible to everyone */}
      <Route path="/pricing" element={<Pricing user={user} />} />

      {/* Subscribe - for existing users to add subscription */}
      <Route
        path="/subscribe"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <Subscribe user={user} />
          </ProtectedRoute>
        }
      />

      {/* Subscription Success - requires auth */}
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <SubscriptionSuccess user={user} />
          </ProtectedRoute>
        }
      />

      {/* Reports - requires auth (has its own sidebar) */}
      <Route
        path="/reports"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            hasAdvancedModules(user) || ownsAddon(user, 'reports') ? <Reports user={user} onLogout={onLogout} /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <AddOnUpgrade moduleId="reports" inline />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Analysis Suite — wraps Underwrite + Financial Analysis under one roof */}
      <Route
        path="/analysis-suite"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              {ownsAddon(user, 'analysis-suite') ? <AnalysisSuite user={user} onLogout={onLogout} /> : <AddOnUpgrade moduleId="analysis-suite" inline />}
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Legacy: /underwrite redirects into the Analysis Suite's Underwrite tab */}
      <Route path="/underwrite" element={<Navigate to="/dashboard" replace />} />

      {/* Contractors — moved out of the (gated) Project Hub so it stays a core,
          always-available feature reachable from the main dashboard. Renders the
          Project Hub in contractors-only mode. */}
      <Route
        path="/contractors"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            <ProjectHub user={user} onLogout={onLogout} contractorsOnly />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Global Documents - requires auth */}
      <Route
        path="/documents"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <GlobalDocuments user={user} onLogout={onLogout} />
          </ProtectedRoute>
        }
      />

      {/* Legacy: /financial-analysis redirects into the Analysis Suite's Project tab */}
      <Route path="/financial-analysis" element={<Navigate to="/dashboard" replace />} />

      {/* Project Hub - requires auth (has its own sidebar). Gated add-on. */}
      <Route
        path="/project-hub"
        element={
          user ? (
            showPaymentModal ? <Navigate to="/membership" replace /> :
            hasAdvancedModules(user) || ownsAddon(user, 'project-hub') ? <ProjectHub user={user} onLogout={onLogout} /> :
            <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
              <AddOnUpgrade moduleId="project-hub" inline />
            </ProtectedRoute>
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Bid Request Detail - requires auth */}
      <Route
        path="/bid-request/:bidRequestId"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <BidRequestDetail user={user} onLogout={onLogout} />
          </ProtectedRoute>
        }
      />

      {/* Standalone Multi-Unit Schedule Wizard — focused workflow without ProjectDetails */}
      <Route
        path="/project/:projectId/schedule-wizard"
        element={
          user ? (
            <StandaloneSchedulePage user={user} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      {/* Contractor Bid Portal - public route */}
      <Route path="/contractor-portal" element={<ContractorPortalV2 />} />

      {/* Legacy path-style lien-waiver links: /contractor-portal/:token.
          These have no dedicated page and used to render blank — redirect to
          the live compliance signing portal so the link resolves. */}
      <Route path="/contractor-portal/:token" element={<ContractorPortalTokenRedirect />} />

      {/* Schedule cascade response - PUBLIC, token-authenticated */}
      <Route path="/cascade/respond" element={<CascadeRespondPage />} />

      {/* Owner one-click email actions - PUBLIC, HMAC-token-authenticated */}
      <Route path="/owner-action/:token" element={<OwnerActionPage />} />

      {/* Schedule timing questionnaire - PUBLIC, HMAC-token-authenticated */}
      <Route path="/schedule-questionnaire/:token" element={<ScheduleQuestionnairePage />} />
      <Route path="/contractor-bid-portal" element={<ContractorBidPortal />} />
      
      {/* Contractor Compliance Portal - for lien waivers, completion photos, inspections */}
      <Route path="/contractor-compliance" element={<ContractorCompliancePortal />} />
      
      {/* Contractor Portal V2 - Multi-phase view with invoice submission */}
      <Route path="/contractor-portal-v2" element={<ContractorPortalV2 />} />
      
      {/* Contractor Login - Email/password login for contractors */}
      <Route path="/contractor-login" element={<ContractorLogin />} />
      
      {/* Contractor Forgot Password - Request password reset */}
      <Route path="/contractor-forgot-password" element={<ContractorForgotPassword />} />
      
      {/* Contractor Reset Password - Set new password with token */}
      <Route path="/contractor-reset-password" element={<ContractorResetPassword />} />
      
      {/* Contractor Self-Signup - Public page for contractors to register */}
      <Route path="/contractor-signup/:signupCode" element={<ContractorSignup />} />
      
      {/* Contractor Portal Preview - Design options comparison */}
      <Route path="/contractor-portal-preview" element={<ContractorPortalPreview />} />

      {/* Admin Routes */}
      <Route path="/admin" element={<Admin />} />
      <Route
        path="/admin/announcements"
        element={
          <ProtectedRoute user={user} onLogout={onLogout} userRoles={userRoles} customPermissions={customPermissions}>
            <FeatureAnnouncementPage />
          </ProtectedRoute>
        }
      />
      <Route path="/admin-login" element={<AdminLogin onLogin={onLogin} />} />

      {/* Legal & Contact Pages - Public */}
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      <Route path="/contact" element={<Contact />} />

      {/* Public SEO Content Infrastructure - marketing/resource pages */}
      <Route path="/resources" element={<ResourcesHub />} />
      <Route path="/resources/construction-draws" element={<ClusterHub cluster="construction-draws" />} />
      <Route path="/resources/construction-loans" element={<ClusterHub cluster="construction-loans" />} />
      <Route path="/resources/lien-waivers" element={<ClusterHub cluster="lien-waivers" />} />
      <Route path="/resources/construction-budgeting" element={<ClusterHub cluster="construction-budgeting" />} />
      <Route path="/resources/:slug" element={<ArticlePage />} />
      <Route path="/glossary" element={<GlossaryIndex />} />
      <Route path="/glossary/:term" element={<GlossaryTerm />} />
      <Route path="/tools" element={<ToolsPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/tools/draw-schedule-builder" element={<DrawScheduleBuilder />} />
      <Route path="/tools/draw-cost-estimator" element={<DrawInterestEstimator />} />
      <Route path="/tools/draw-readiness-checklist" element={<DrawReadinessChecklist />} />
      {PRODUCTS.map((p) => (
        <Route key={p.slug} path={`/${p.slug}`} element={<ProductPage slug={p.slug} />} />
      ))}

      {/* Safety net — never render a blank screen for an unmatched URL. */}
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
    
    {/* Global Tour - renders on top of everything, persists across navigation */}
    {user && !user.is_admin && (
      <>
        <WelcomeTour
          isOpen={showTour}
          onClose={closeTour}
          onComplete={closeTour}
        />
        
        {/* Floating Take a Tour button - visible when tour is not active */}
        {hasSeenTour && !showTour && (
          <button
            onClick={startTour}
            className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white pl-4 pr-5 py-3 rounded-full shadow-lg transition-all hover:scale-105 flex items-center gap-2"
            title="Take a Tour"
            data-testid="restart-tour-btn"
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-sm font-medium">Take a Tour</span>
          </button>
        )}
      </>
    )}
    </>
  );
}

function App() {
  // Initialize user from localStorage synchronously to prevent redirect flicker on refresh
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [isChatbotOpen, setIsChatbotOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showFeatureAnnouncement, setShowFeatureAnnouncement] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [userRoles, setUserRoles] = useState([]); // Team roles for current user
  const [customPermissions, setCustomPermissions] = useState([]); // Custom permissions for investors

  // Fetch user's team roles
  const fetchUserRoles = useCallback(async (userData) => {
    if (!userData?.id) return;
    
    // If user is account owner (not team_member), they have full access
    if (userData.account_type !== 'team_member') {
      setUserRoles([]);
      setCustomPermissions([]);
      return;
    }
    
    try {
      const response = await apiService.getMyTeamAccess(userData.id);
      if (response.data && response.data.length > 0) {
        const access = response.data[0];
        const roles = access.roles && access.roles.length > 0 
          ? access.roles 
          : [access.role || 'investor'];
        setUserRoles(roles);
        
        // Get custom permissions if available
        const permissions = access.custom_permissions || [];
        setCustomPermissions(permissions);
      }
    } catch (err) {
      console.error('Failed to fetch user roles:', err);
      setUserRoles([]);
      setCustomPermissions([]);
    }
  }, []);

  // Check subscription status when user is logged in
  const checkSubscriptionStatus = useCallback(async (userData) => {
    if (!userData?.id) return;
    try {
      const { data } = await apiService.getSubscriptionStatus(userData.id);
      // Hard gate only once the trial has actually ended without a card on file.
      // While still in trial, card-less users keep full access and are nudged
      // by the in-app TrialCardBanner instead (see components/Sidebar layout).
      if (data.card_required && data.trial_expired) {
        setShowPaymentModal(true);
      } else {
        setShowPaymentModal(false);
      }
    } catch (err) {
      console.error('Failed to check subscription status:', err);
      // Don't block on error - allow access
      setShowPaymentModal(false);
    }
    
    // Also fetch user roles
    await fetchUserRoles(userData);

    // Sync latest feature flags (e.g. owner-only demo-tool visibility) so flag
    // changes propagate on refresh without forcing a full re-login.
    try {
      const { data: flagsData } = await apiService.getFeatureFlags(userData.id);
      const latestFlags = flagsData?.feature_flags || {};
      setUser(prev => {
        if (!prev || prev.id !== userData.id) return prev;
        const merged = { ...prev, feature_flags: { ...(prev.feature_flags || {}), ...latestFlags } };
        if (JSON.stringify(prev.feature_flags || {}) === JSON.stringify(merged.feature_flags)) return prev;
        try { localStorage.setItem('user', JSON.stringify(merged)); } catch (e) { /* ignore */ }
        return merged;
      });
    } catch (e) { /* ignore — non-blocking */ }
  }, [fetchUserRoles]);

  // Mark as hydrated after initial render to prevent any redirect issues on tab focus
  useEffect(() => {
    setIsHydrated(true);
    
    // Check subscription on mount if user exists
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        checkSubscriptionStatus(parsed);
        // v2.4: also fire WhatsNew check on mount (covers browser refresh /
        // tab reopen for already-logged-in users). The modal self-suppresses
        // when /whatsnew/latest returns no entry, so safe to fire freely.
        // Skip for public demo visitors.
        if (parsed?.id && !localStorage.getItem('demo_session')) {
          setTimeout(() => setShowWhatsNew(true), 700);
        }
      } catch (e) { /* ignore */ }
    }
    
    // Re-sync user from localStorage when tab becomes visible again (iOS Safari fix)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(prevUser => {
              // Only update if user data actually changed
              if (JSON.stringify(prevUser) !== JSON.stringify(parsedUser)) {
                return parsedUser;
              }
              return prevUser;
            });
          } catch (e) {
            console.error('Failed to parse stored user:', e);
          }
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []); // Only run on mount

  const handleLogin = (userData) => {
    // Check if login response includes feature announcement flag
    const needsFeatureAnnouncement = userData.show_feature_announcement === true;
    const isDemo = !!userData.demo_session;

    // Persist the Bearer session token so authenticated API calls work.
    // Normal logins go through the `api` axios instance (whose response
    // interceptor saves this automatically), but the public demo flow posts
    // via raw axios — so without this, the demo has no cookie AND no Bearer,
    // every dashboard call 401s, and the visitor is booted back to /login.
    if (userData.session_token) {
      try { localStorage.setItem('dr_session_token', userData.session_token); } catch (e) { /* ignore */ }
    }

    // Store user data (without the temporary flags)
    const userToStore = { ...userData };
    delete userToStore.show_feature_announcement;
    delete userToStore.current_feature_version;
    delete userToStore.demo_session;
    delete userToStore.session_token;

    setUser(userToStore);
    localStorage.setItem('user', JSON.stringify(userToStore));
    // Persistent onboarding gate flag (survives user-object refetches/races).
    if (userToStore.onboarding_completed) localStorage.setItem('onboarding_done', '1');
    else localStorage.removeItem('onboarding_done');
    
    // Check subscription after login
    checkSubscriptionStatus(userToStore);
    
    // Fire What's New modal on every login (it self-suppresses if user is current).
    // Skip entirely for public demo visitors — they shouldn't see internal changelogs.
    if (!isDemo) {
      setTimeout(() => setShowWhatsNew(true), 700);
    }
    
    // Show feature announcement modal if needed (after a small delay to let navigation settle)
    if (needsFeatureAnnouncement) {
      setTimeout(() => setShowFeatureAnnouncement(true), 500);
    }
  };

  const handleFeatureLayoutSelected = (selectedLayout) => {
    setShowFeatureAnnouncement(false);
    // Update user with new dashboard preference
    if (user) {
      const updatedUser = { ...user, dashboard_preference: selectedLayout };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const handleUpdateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const handleLogout = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    try {
      apiService.logout();
    } catch (e) { /* best-effort server session invalidation */ }
    try {
      localStorage.removeItem('dr_session_token');
      localStorage.removeItem('drawready.lastPath');
      localStorage.removeItem('onboarding_done');
      sessionStorage.removeItem('lastPath');
    } catch (e) { /* ignore */ }
    setIsChatbotOpen(false);
    setShowPaymentModal(false);
  }, []);

  const handlePaymentSuccess = useCallback(() => {
    setShowPaymentModal(false);
    // Refresh subscription status
    if (user) {
      checkSubscriptionStatus(user);
    }
  }, [user, checkSubscriptionStatus]);

  // Auto-logout after 30 minutes of inactivity (matches server session idle timeout).
  // While actively using the app, ping the server to slide its session so a user
  // typing a long form isn't logged out mid-task.
  const keepSessionAlive = useCallback(() => {
    apiService.authMe().catch(() => { /* expired -> interceptor handles it */ });
  }, []);
  useIdleTimeout(
    30 * 60 * 1000, // 30 minutes
    handleLogout,
    !!user, // Only enable when user is logged in
    keepSessionAlive
  );

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <PreviewEnvBanner />
      <AppRouter 
        user={user} 
        onLogin={handleLogin} 
        onLogout={handleLogout} 
        onUpdateUser={handleUpdateUser}
        isHydrated={isHydrated}
        showPaymentModal={showPaymentModal}
        onPaymentComplete={handlePaymentSuccess}
        userRoles={userRoles}
        customPermissions={customPermissions}
      />
      
      {/* Global AI Assistant - Available on all authenticated pages */}
      {user && !showPaymentModal && (
        <>
          <AssistantWidget user={user} />

          {/* Quick Capture - Snap a receipt from anywhere; AI files it */}
          <QuickCaptureFAB />

          {/* Draw confirmation nudge — unmissable popup on login for draws awaiting deposit confirmation */}
          <DrawConfirmationManager user={user} />
        </>
      )}
      
      {/* Feature Announcement Modal - Shown on login when user hasn't seen new features */}
      {user && showFeatureAnnouncement && (
        <FeatureAnnouncementModal 
          isOpen={showFeatureAnnouncement}
          userId={user.id}
          onLayoutSelected={handleFeatureLayoutSelected}
        />
      )}
      {user && showWhatsNew && (
        <WhatsNewModal
          userId={user.id}
          onClose={() => setShowWhatsNew(false)}
        />
      )}
    </BrowserRouter>
  );
}

export default App;
